import * as THREE from 'three';
import { ThreeEvent } from '@react-three/fiber';

import { AppState } from '../state/store';
import { BIMState } from '../state/useBIMStore';
import { BIMElement, Constraint, LevelElement } from '../core/types';
import { HostManager } from '../core/HostManager';
import { raycastSceneAtPointer } from '../utils/scenePicking';
import { useAppStore } from '../state/store';

export interface ToolContext {
    scene: THREE.Scene;
    camera: THREE.Camera;
    renderer: THREE.WebGLRenderer;
    raycaster: THREE.Raycaster;
    appStore: AppState;
    bimStore: BIMState;
}

export interface Tool {
    id: string;
    name: string;
    cursor: string;

    // Lifecycle
    activate(context: ToolContext): void;
    deactivate(): void;

    // Events
    onPointerDown(event: ThreeEvent<PointerEvent>): void;
    onPointerMove(event: ThreeEvent<PointerEvent>): void;
    onPointerUp(event: ThreeEvent<PointerEvent>): void;
    onKeyDown(event: KeyboardEvent): void;
    onKeyUp?(event: KeyboardEvent): void;

    // Render
    renderPreview?(): React.ReactNode;
}

/**
 * Preview state for tool rendering
 */
export interface ToolPreviewState {
    isValid: boolean;
    geometry?: THREE.BufferGeometry;
    position?: THREE.Vector3;
    rotation?: THREE.Euler;
    color?: string;
    opacity?: number;
    snapPoint?: THREE.Vector3;
    snapType?: 'endpoint' | 'midpoint' | 'intersection' | 'perpendicular' | 'grid';
}

/**
 * Constraint builder for element relationships
 */
export interface ConstraintBuilder {
    type: Constraint['type'];
    targetId: string;
    data?: Record<string, unknown>;
}

export abstract class BaseTool implements Tool {
    abstract id: string;
    abstract name: string;
    abstract cursor: string;

    protected context: ToolContext | null = null;
    protected previewState: ToolPreviewState = { isValid: false };
    protected isOrthoMode: boolean = false;
    protected snapAngle: number = 45; // degrees for angle snapping
    protected hostManager: HostManager | null = null;

    activate(context: ToolContext): void {
        this.context = context;
        this.hostManager = new HostManager(context.bimStore);
        this.previewState = { isValid: false };
        // Dialog/utility tools implement their whole behavior here; without
        // this call they would stay inert after activation.
        (this as unknown as { onActivate?: () => void }).onActivate?.();
    }

    deactivate(): void {
        this.clearPreview();
        this.context = null;
        this.hostManager = null;
    }

    /**
     * Finish a dialog-driven tool and hand the viewport back to Select. The
     * store handle must be captured BEFORE deactivate() nulls the context —
     * calling `this.context?.appStore.setActiveTool(...)` after deactivation
     * is a silent no-op that leaves the tool id stuck in the store, so the
     * same ribbon button would do nothing when clicked again.
     */
    protected exitToSelect(): void {
        const appStore = this.context?.appStore;
        this.deactivate();
        appStore?.setActiveTool('select');
    }

    abstract onPointerDown(event: ThreeEvent<PointerEvent>): void;
    abstract onPointerMove(event: ThreeEvent<PointerEvent>): void;
    abstract onPointerUp(event: ThreeEvent<PointerEvent>): void;

    /**
     * Optional one-shot activation hook for dialog/utility tools.
     * Called by activate(); override in subclasses that complete their work
     * at activation time instead of waiting for pointer events.
     */
    protected onActivate?(): void;

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            this.clearPreview();
            this.context?.appStore.setActiveTool('select');
        }
        // Toggle ortho mode with Shift
        if (event.key === 'Shift') {
            this.isOrthoMode = true;
        }
    }

    /**
     * Handle key up events (override in subclasses)
     */
    onKeyUp?(event: KeyboardEvent): void {
        if (event.key === 'Shift') {
            this.isOrthoMode = false;
        }
    }

    // ==================== Preview Rendering ====================

    /**
     * Update preview state - call this in onPointerMove
     */
    protected updatePreviewState(state: Partial<ToolPreviewState>): void {
        this.previewState = { ...this.previewState, ...state };
        if (this.context) {
            this.context.appStore.setToolPreview(this.previewState);
        }
    }

    /**
     * Clear preview state
     */
    protected clearPreview(): void {
        this.previewState = { isValid: false };
        if (this.context) {
            this.context.appStore.setToolPreview(null);
        }
    }

    /**
     * Dispatch a toast notification to the global NotificationHost so tool
     * feedback (file picked, command finished, errors) reaches the user.
     */
    protected notify(message: string, status: 'info' | 'success' | 'warning' | 'error' = 'info'): void {
        if (typeof window === 'undefined') return; // Non-DOM test environments
        window.dispatchEvent(
            new CustomEvent('yap:notify', { detail: { message, status } })
        );
    }

    /**
     * Open a native file picker and deliver the selected file to the tool.
     * Dialog-driven Insert/Link commands share this flow; resolves null when
     * the dialog is dismissed. In non-DOM environments (tests) it resolves
     * null immediately so activation stays side-effect free.
     */
    protected pickFile(accept: string): Promise<File | null> {
        if (typeof document === 'undefined') {
            return Promise.resolve(null);
        }
        return new Promise((resolve) => {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = accept;
            input.onchange = () => {
                resolve(input.files?.[0] ?? null);
            };
            input.oncancel = () => resolve(null);
            input.click();
        });
    }

    // ==================== Constraint Handling ====================

    /**
     * Create a host constraint (e.g., door hosted by wall)
     */
    protected createHostConstraint(hostId: string, positionAlongHost?: number): ConstraintBuilder {
        return {
            type: 'HOST_CONTAINED',
            targetId: hostId,
            data: positionAlongHost !== undefined ? { positionAlongHost } : undefined
        };
    }

    /**
     * Create a level constraint
     */
    protected createLevelConstraint(levelId: string, offset: number = 0): ConstraintBuilder {
        return {
            type: 'LEVEL_BASED',
            targetId: levelId,
            data: { offset }
        };
    }

    /**
     * Create an alignment constraint
     */
    protected createAlignmentConstraint(targetId: string, alignmentType: 'horizontal' | 'vertical'): ConstraintBuilder {
        return {
            type: 'ALIGNED',
            targetId,
            data: { alignmentType }
        };
    }

    /**
     * Build constraints array from builders
     */
    protected buildConstraints(builders: ConstraintBuilder[]): Constraint[] {
        return builders.map(b => ({
            type: b.type,
            targetId: b.targetId,
            data: b.data
        }));
    }

    // ==================== Level Association ====================

    /**
     * Get levels from BIM store
     */
    protected getLevels(): Map<string, LevelElement> {
        if (!this.context) return new Map();

        const levels = new Map<string, LevelElement>();
        this.context.bimStore.project.elements.forEach((el, id) => {
            if (el.category === 'Levels') {
                levels.set(id, el as LevelElement);
            }
        });
        return levels;
    }

    /**
     * Find the closest level to a given elevation
     */
    protected findClosestLevel(elevation: number): LevelElement | null {
        const levels = this.getLevels();
        let closest: LevelElement | null = null;
        let minDist = Infinity;

        levels.forEach(level => {
            const levelElev = level.geometryData?.elevation ?? 0;
            const dist = Math.abs(levelElev - elevation);
            if (dist < minDist) {
                minDist = dist;
                closest = level;
            }
        });

        return closest;
    }

    /**
     * Find level at or below given elevation
     */
    protected findLevelAtOrBelow(elevation: number): LevelElement | null {
        const levels = Array.from(this.getLevels().values());
        const sortedLevels = levels.sort((a, b) =>
            (b.geometryData?.elevation ?? 0) - (a.geometryData?.elevation ?? 0)
        );

        for (const level of sortedLevels) {
            const levelElev = level.geometryData?.elevation ?? 0;
            if (levelElev <= elevation + 0.001) {
                return level;
            }
        }

        return sortedLevels[sortedLevels.length - 1] || null;
    }

    /**
     * Get level elevation by ID
     */
    protected getLevelElevation(levelId: string): number {
        const levels = this.getLevels();
        const level = levels.get(levelId);
        return level?.geometryData?.elevation ?? 0;
    }

    // ==================== Intersection Helpers ====================

    /**
     * Get intersection with ground plane or objects
     */
    protected getIntersection(event: ThreeEvent<PointerEvent>, planeHeight: number = 0): THREE.Vector3 | null {
        if (!this.context) return null;

        // Do NOT trust event.ray: on the first pointer event after tool
        // activation it can carry a ray computed against another event
        // layer's camera. Recompute the ray from the native pointer position
        // against the current default camera instead.
        const domElement = this.context.renderer.domElement;
        const rect = domElement.getBoundingClientRect();
        // A transiently empty rect (first paint / mid-layout) would collapse
        // the NDC math onto a single point and land the intersection near the
        // world origin — bail out instead of producing a bogus point.
        if (rect.width === 0 || rect.height === 0) return null;
        BaseTool._ndcScratch.set(
            ((event.nativeEvent.clientX - rect.left) / rect.width) * 2 - 1,
            -((event.nativeEvent.clientY - rect.top) / rect.height) * 2 + 1
        );
        const camera = this.context.camera;
        // setFromCamera reads matrixWorld directly; on the first pointer
        // event after activation (or right after a pan/zoom with
        // frameloop="demand") it can still hold the previous frame's
        // transform, which bends the ray away from the actual click.
        camera.updateMatrixWorld();
        this.context.raycaster.setFromCamera(BaseTool._ndcScratch, camera);

        // Reused plane/point: pointermove flows through here at device rate,
        // so per-event Vector3/Plane allocations showed up as GC pressure.
        BaseTool._planeScratch.constant = -planeHeight;
        const hit = this.context.raycaster.ray.intersectPlane(BaseTool._planeScratch, BaseTool._pointScratch);

        // Return null on miss (ray parallel to / pointing away from the plane).
        // The previous code returned the untouched (0,0,0) fallback point.
        // Clone for the caller: the scratch is overwritten by the next event.
        return hit ? BaseTool._pointScratch.clone() : null;
    }

    private static readonly _ndcScratch = new THREE.Vector2();
    private static readonly _planeScratch = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
    private static readonly _pointScratch = new THREE.Vector3();

    /**
     * Get intersection with specific category of elements
     */
    protected getElementIntersection(
        event: ThreeEvent<PointerEvent>,
        category: string
    ): { element: BIMElement; point: THREE.Vector3 } | null {
        if (!this.context) return null;

        const hit = event.intersections.find(i => {
            const id = this.findId(i.object);
            const el = id ? this.context!.bimStore.project.elements.get(id) : null;
            return el && el.category === category;
        });

        if (hit) {
            const id = this.findId(hit.object)!;
            const element = this.context.bimStore.project.elements.get(id)!;
            return { element, point: hit.point.clone() };
        }

        return null;
    }

    // ==================== Element Picking ====================

    /**
     * Raycast the whole scene under the pointer and return the sorted hits.
     *
     * R3F only raycasts objects that carry event handlers, and BIM element
     * meshes have none — tool events arrive via the invisible event-catching
     * plane, so event.object/event.intersections can never resolve a BIM
     * element. Manual scene raycasting (see raycastSceneAtPointer) is the
     * single reliable pick path.
     */
    protected raycastSceneAtEvent(event: ThreeEvent<PointerEvent>): THREE.Intersection[] {
        if (!this.context) return [];
        return raycastSceneAtPointer(
            this.context.raycaster,
            this.context.camera,
            this.context.scene,
            this.context.renderer.domElement,
            event.nativeEvent.clientX,
            event.nativeEvent.clientY
        );
    }

    /**
     * Resolve a selectable BIM element id from a scene hit. Handles instanced
     * BIM bulk geometry (bimInstanceIds + instanceId) and walks up the parent
     * chain for userData.id, verifying membership in the BIM store.
     */
    protected resolveElementIdFromHit(hit: THREE.Intersection): string | null {
        if (!this.context) return null;

        const instanceIds = hit.object.userData?.bimInstanceIds as string[] | undefined;
        if (instanceIds && hit.instanceId != null) {
            const instancedId = instanceIds[hit.instanceId];
            if (instancedId && this.context.bimStore.project.elements.has(instancedId)) {
                return instancedId;
            }
        }

        let current: THREE.Object3D | null = hit.object;
        while (current) {
            if (current.userData?.id) {
                const id = current.userData.id;
                if (this.context.bimStore.project.elements.has(id)) return id;
                if (current.userData.levelId) return current.userData.levelId;
            }
            current = current.parent;
        }
        return null;
    }

    /**
     * True for hits that must never swallow a pick: invisible helpers, the
     * invisible event-catching plane itself, and wall shape handles (they
     * sit in front of the wall body but own their own drag gesture).
     */
    protected isUnpickableHit(hit: THREE.Intersection): boolean {
        return !hit.object.visible
            || hit.object.userData?.isEventPlane === true
            || hit.object.userData?.isWallHandle === true;
    }

    /**
     * Collect EVERY distinct selectable element id from a sorted hit list,
     * front-to-back. Shared by the pointer-position and event-based cycling
     * collectors so the hover stack and Tab preview agree on ordering.
     */
    protected collectElementIdsFromHits(hits: THREE.Intersection[]): string[] {
        const ids: string[] = [];
        const seen = new Set<string>();
        for (const hit of hits) {
            if (this.isUnpickableHit(hit)) continue;
            const id = this.resolveElementIdFromHit(hit);
            if (id && !seen.has(id)) {
                seen.add(id);
                ids.push(id);
            }
        }
        return ids;
    }

    /**
     * Collect EVERY distinct selectable element id under a screen position,
     * front-to-back. Powers BIM-style hover cycling (Tab walks through the
     * stack of overlapping elements under the cursor).
     */
    protected collectElementIdsAtPointer(clientX: number, clientY: number): string[] {
        if (!this.context) return [];
        const hits = raycastSceneAtPointer(
            this.context.raycaster,
            this.context.camera,
            this.context.scene,
            this.context.renderer.domElement,
            clientX,
            clientY
        );
        return this.collectElementIdsFromHits(hits);
    }

    /**
     * Find the element id under the pointer: raycast the scene and return the
     * first hit that maps to a selectable element.
     */
    protected findElementIdAtPoint(event: ThreeEvent<PointerEvent>): string | null {
        const hits = this.raycastSceneAtEvent(event);
        for (const hit of hits) {
            if (this.isUnpickableHit(hit)) continue;
            const id = this.resolveElementIdFromHit(hit);
            if (id) return id;
        }
        return null;
    }

    /**
     * Find the first scene hit under the pointer that resolves to a BIM
     * element passing the optional category/property filter. Returns the
     * element with its hit point so tools can act on the clicked side/face.
     */
    protected findElementHitAtPoint(
        event: ThreeEvent<PointerEvent>,
        filter?: (element: BIMElement) => boolean
    ): { element: BIMElement; point: THREE.Vector3 } | null {
        const hits = this.raycastSceneAtEvent(event);
        for (const hit of hits) {
            if (this.isUnpickableHit(hit)) continue;
            const id = this.resolveElementIdFromHit(hit);
            if (!id) continue;
            const element = this.context!.bimStore.project.elements.get(id);
            if (!element) continue;
            if (filter && !filter(element)) continue;
            return { element, point: hit.point.clone() };
        }
        return null;
    }

    /** Select an element in both stores so the highlight and tool state agree. */
    protected selectElement(id: string): void {
        if (!this.context) return;
        this.context.appStore.select([id]);
        this.context.bimStore.setSelection([id]);
    }

    // ==================== Snapping Helpers ====================

    /**
     * Snap point to grid
     */
    protected snapToGrid(point: THREE.Vector3, gridSize: number = 0.5): THREE.Vector3 {
        return new THREE.Vector3(
            Math.round(point.x / gridSize) * gridSize,
            Math.round(point.y / gridSize) * gridSize,
            point.z
        );
    }

    /**
     * Snap to ortho (horizontal/vertical) from reference point
     */
    protected snapToOrtho(point: THREE.Vector3, reference: THREE.Vector3): THREE.Vector3 {
        const dx = Math.abs(point.x - reference.x);
        const dy = Math.abs(point.y - reference.y);

        if (dx > dy) {
            return new THREE.Vector3(point.x, reference.y, point.z);
        } else {
            return new THREE.Vector3(reference.x, point.y, point.z);
        }
    }

    /**
     * Snap to angle increments from reference point
     */
    protected snapToAngle(point: THREE.Vector3, reference: THREE.Vector3, angleIncrement: number = 45): THREE.Vector3 {
        const dx = point.x - reference.x;
        const dy = point.y - reference.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < 0.001) return point.clone();

        const angle = Math.atan2(dy, dx);
        const snapAngleRad = (angleIncrement * Math.PI) / 180;
        const snappedAngle = Math.round(angle / snapAngleRad) * snapAngleRad;

        return new THREE.Vector3(
            reference.x + distance * Math.cos(snappedAngle),
            reference.y + distance * Math.sin(snappedAngle),
            point.z
        );
    }

    /**
     * Apply ortho/angle snapping if enabled
     */
    protected applyDirectionalSnap(point: THREE.Vector3, reference: THREE.Vector3 | null): THREE.Vector3 {
        if (!reference) return point;

        // Ortho engages from the persisted F8 toggle or the temporary Shift
        // hold — the local flag alone would miss the status-bar toggle.
        const app = useAppStore.getState();
        if (this.isOrthoMode || app.orthoMode || app.shiftOrthoActive) {
            return this.snapToOrtho(point, reference);
        }

        return point;
    }

    // ==================== Element ID Helpers ====================

    /**
     * Find element ID from Three.js object
     */
    protected findId(obj: THREE.Object3D): string | null {
        let current: THREE.Object3D | null = obj;
        while (current) {
            if (current.userData && current.userData.id) {
                return current.userData.id;
            }
            current = current.parent;
        }
        return null;
    }

    /**
     * Find element from Three.js object
     */
    protected findElement(obj: THREE.Object3D): BIMElement | null {
        const id = this.findId(obj);
        if (!id || !this.context) return null;
        return this.context.bimStore.project.elements.get(id) || null;
    }

    // ==================== Distance/Measurement Helpers ====================

    /**
     * Calculate distance between two points in 2D (XY plane)
     */
    protected distance2D(p1: THREE.Vector3, p2: THREE.Vector3): number {
        return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
    }

    /**
     * Calculate angle between two points in degrees
     */
    protected angleBetween(p1: THREE.Vector3, p2: THREE.Vector3): number {
        return Math.atan2(p2.y - p1.y, p2.x - p1.x) * (180 / Math.PI);
    }

    /**
     * Get midpoint between two points
     */
    protected midpoint(p1: THREE.Vector3, p2: THREE.Vector3): THREE.Vector3 {
        return new THREE.Vector3(
            (p1.x + p2.x) / 2,
            (p1.y + p2.y) / 2,
            (p1.z + p2.z) / 2
        );
    }
}
